import type { PackageSubjectLinkInput, PackageWithLinksInput } from "@models/ISchema";
import type { PackageWithLeaguesState } from "../../store/packageWithLeaguesStore";
import { selectShouldVisitInitialGroups } from "../../store/packageWithLeaguesStore";
import type { SubjectConfiguration } from "../../types";
import { hasInvalidVacancySource } from "../group-vacancy-source";

export interface OrderedSubjectSnapshot {
  courseId: string;
  choiceKind: "create-new" | "use-existing";
  hasInitialGroup: boolean;
  input: PackageSubjectLinkInput;
}

export interface OrderedPackageWithLinksRequest {
  input: PackageWithLinksInput;
  subjects: OrderedSubjectSnapshot[];
}

export class PackageConfigurationError extends Error {
  constructor(
    readonly reason: string,
    readonly courseId?: string,
    readonly field?: "group-code" | "group-vacancies",
  ) {
    super(reason);
    this.name = "PackageConfigurationError";
  }
}

const parseGroupCapacity = (value: string, courseId: string): number => {
  const capacity = Number(value);
  if (!value || !Number.isInteger(capacity) || capacity <= 0) {
    throw new PackageConfigurationError("GROUP_VACANCIES_REQUIRED", courseId, "group-vacancies");
  }
  return capacity;
};

const buildSubjectSnapshot = (
  state: PackageWithLeaguesState,
  configuration: SubjectConfiguration,
): OrderedSubjectSnapshot | null => {
  const { subject, choice } = configuration;
  if (choice.kind === "omit") return null;

  if (choice.kind === "use-existing") {
    if (!choice.existingLeagueId) {
      throw new PackageConfigurationError("EXISTING_LEAGUE_REQUIRED", subject.courseId);
    }

    return {
      courseId: subject.courseId,
      choiceKind: "use-existing",
      hasInitialGroup: false,
      input: {
        courseId: subject.courseId,
        existingLinkId: choice.existingLeagueId,
      },
    };
  }

  if (!choice.inferredCode) {
    throw new PackageConfigurationError("LINK_CODE_INFERENCE_FAILED", subject.courseId);
  }

  const hasInitialGroup = selectShouldVisitInitialGroups(state);
  if (hasInitialGroup && hasInvalidVacancySource(subject, choice.group)) {
    throw new PackageConfigurationError(
      "GROUP_VACANCIES_SOURCE_INVALID",
      subject.courseId,
      "group-vacancies",
    );
  }
  if (hasInitialGroup && !choice.group.code) {
    throw new PackageConfigurationError(
      "GROUP_CODE_REQUIRED_OR_INVALID",
      subject.courseId,
      "group-code",
    );
  }
  const createGroup = hasInitialGroup
    ? {
        programId:
          state.packageDetails.population?.programId || state.packageDetails.programId || undefined,
        code: choice.group.code,
        capacity: parseGroupCapacity(choice.group.vacancies, subject.courseId),
      }
    : undefined;

  return {
    courseId: subject.courseId,
    choiceKind: "create-new",
    hasInitialGroup,
    input: {
      courseId: subject.courseId,
      newLink: {
        courseId: subject.courseId,
        termId: state.packageDetails.termId,
        code: choice.inferredCode,
        assignSections: choice.sections
          .filter(section => section.kind === "use-existing")
          .map(section => section.sectionId),
        createSections: choice.sections
          .filter(section => section.kind === "create-new")
          .map(section => ({ courseComponentId: section.courseComponentId })),
        createGroup,
      },
    },
  };
};

export const buildOrderedPackageWithLinksRequest = (
  state: PackageWithLeaguesState,
  dryRun: boolean,
  excludedCourseIds: ReadonlySet<string> = new Set(),
): OrderedPackageWithLinksRequest => {
  if (!state.packageDetails.population) {
    throw new PackageConfigurationError("PACKAGE_POPULATION_REQUIRED");
  }

  const subjects = state.subjects
    .filter(configuration => !excludedCourseIds.has(configuration.subject.courseId))
    .map(configuration => buildSubjectSnapshot(state, configuration))
    .filter((snapshot): snapshot is OrderedSubjectSnapshot => Boolean(snapshot));

  return {
    input: {
      dryRun,
      skipValidations: false,
      package: {
        termId: state.packageDetails.termId,
        populationCode: state.packageDetails.population.code,
        packageName: state.packageDetails.packageName,
      },
      subjects: subjects.map(snapshot => snapshot.input),
    },
    subjects,
  };
};

export const toCommitRequest = (
  request: OrderedPackageWithLinksRequest,
): OrderedPackageWithLinksRequest => ({
  input: { ...request.input, dryRun: false, skipValidations: false },
  subjects: request.subjects,
});
